/**
 * Constants Module Entry Point
 *
 * Application-wide constant values.
 */

// =============================================================================
// API CONFIGURATION
// =============================================================================

export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_SERVER_URL || '',
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

// =============================================================================
// USER ROLES
// =============================================================================

export const USER_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  COACH: 'COACH',
  USER: 'USER',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// =============================================================================
// LOCAL STORAGE KEYS
// =============================================================================

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'evaltick_auth_token',
  USER_PREFERENCES: 'evaltick_user_preferences',
  THEME: 'evaltick_theme',
  SIDEBAR_COLLAPSED: 'evaltick_sidebar_collapsed',
  ONBOARDING_COMPLETED: 'evaltick_onboarding_completed',
  SESSION_WIZARD: 'evaltick_session_wizard',
  NEXT_PART_PROMPTED: 'evaltick_next_part_prompted',
} as const;

// =============================================================================
// QUERY KEYS (for React Query / data fetching)
// =============================================================================

export const QUERY_KEYS = {
  USER: 'user',
  USER_PROFILE: 'user-profile',
  USER_PREFERENCES: 'user-preferences',
  AGENTS: 'agents',
  AGENT_THREADS: 'agent-threads',
  AGENT_MESSAGES: 'agent-messages',
} as const;

// =============================================================================
// FEATURE FLAGS
// =============================================================================

export const FEATURES = {
  DARK_MODE: import.meta.env.VITE_FEATURE_DARK_MODE === 'true',
  ANALYTICS: import.meta.env.VITE_FEATURE_ANALYTICS === 'true',
  // Needs ANALYTICS on too. Keep off until privacy and consent sign-off.
  SESSION_REPLAY: import.meta.env.VITE_FEATURE_SESSION_REPLAY === 'true',
  DEBUG_MODE: import.meta.env.DEV,
} as const;

// =============================================================================
// PAGINATION DEFAULTS
// =============================================================================

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
} as const;

// =============================================================================
// VALIDATION
// =============================================================================

export const VALIDATION = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
} as const;

// =============================================================================
// DATE/TIME
// =============================================================================

export const DATE_FORMATS = {
  DATE: 'MMM d, yyyy',
  DATE_TIME: 'MMM d, yyyy h:mm a',
  TIME: 'h:mm a',
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
} as const;

// =============================================================================
// LIQUID GLASS
// =============================================================================

export { FORM_GLASS_SETTINGS } from './glass';
